// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <https://benrichardson.dev>
// Based on QuakeWatch by Ben Richardson — https://benrichardson.dev
import { svgCumulativeLine, svgHistogram, svgTreemap, type HistBin } from '../components/charts';
import { openQuakeDrawer } from '../components/detail';
import { gterm, infoI } from '../components/overlays';
import { dec, esc, joules, mag as fmtMag } from '../format';
import {
  bValue, completenessMagnitude, energyJoules, magColor, magFrequency, totalEnergy,
} from '../model';
import { quakes } from '../state';
import { emptyState } from './shared';

let logY = true;

export function renderMagnitude(root: HTMLElement): void {
  const list = quakes();
  if (list.length === 0) { root.innerHTML = emptyState(); return; }

  const bins = magFrequency(list, 0.5);
  const b = bValue(list, 0.1);
  const mc = completenessMagnitude(list, 0.5);
  const maxCount = Math.max(...bins.map((x) => x.count), 1);
  const histBins: HistBin[] = bins.map((bin) => ({
    label: bin.lo.toFixed(1),
    count: bin.count,
    color: magColor(bin.mid),
    tip: `M ${bin.lo.toFixed(1)}–${bin.hi.toFixed(1)}: ${bin.count} events\n${bin.cum.toLocaleString()} at or above M ${bin.lo.toFixed(1)}`,
  }));

  // energy treemap — biggest energy contributors
  const byEnergy = [...list].sort((a, b2) => energyJoules(b2.mag) - energyJoules(a.mag)).slice(0, 40);
  const totalE = totalEnergy(list);
  const treeItems = byEnergy.map((q) => ({
    value: energyJoules(q.mag),
    label: `M${fmtMag(q.mag)}`,
    color: magColor(q.mag),
    tip: `M ${fmtMag(q.mag)} · ${esc(q.place)}\n${joules(energyJoules(q.mag))} (${Math.round((energyJoules(q.mag) / totalE) * 100)}% of window)`,
    id: q.id,
  }));

  root.innerHTML = `
    <div class="panel">
      <div class="between">
        <div>
          <div class="panel-title">The ${gterm('gutenberg_richter', 'Gutenberg–Richter law')}</div>
          <div class="panel-sub" style="margin:0">Earthquake frequency versus size. Small quakes hugely outnumber large ones — each step up in magnitude is roughly ten times rarer.</div>
        </div>
        <button class="pill-btn ${logY ? 'active' : ''}" id="toggle-log">Log scale ${infoI('gutenberg_richter')}</button>
      </div>
      <div class="chart-wrap" style="margin-top:0.75rem">${svgHistogram(histBins, { height: 260, log: logY })}</div>
      <div class="legend"><span>Bars: number of events per 0.5-magnitude bin${logY ? ' (log height)' : ''}. Line: cumulative count at or above each magnitude.</span></div>
      <div class="chart-wrap" style="margin-top:0.5rem">${svgCumulativeLine(histBins, bins.map((x) => x.cum), { height: 200 })}</div>
      <div class="grid cols-auto" style="margin-top:1rem">
        ${stat(Number.isFinite(b) ? dec(b, 2) : '—', 'b-value', 'slope of the law — near 1.0 worldwide')}
        ${stat('M ' + mc.toFixed(1), 'completeness', 'smallest reliably-detected size')}
        ${stat(bins.reduce((s, x) => (x.count > s.count ? x : s), bins[0]).lo.toFixed(1), 'most common size', 'the modal magnitude bin')}
      </div>
      <div class="callout mt-lg">
        <strong>What the ${gterm('b_value', 'b-value')} means.</strong> It measures how steeply earthquake
        numbers fall off as magnitude rises. A value near 1.0 (typical) means about ten times as many
        M4s as M5s. The curve bending down at the smallest magnitudes is the ${gterm('completeness', 'magnitude of completeness')} —
        below it, many tiny quakes simply aren’t detected.
      </div>
    </div>

    <div class="panel" style="margin-top:1rem">
      <div class="panel-title">Where the energy went ${infoI('energy')}</div>
      <div class="panel-sub">Each tile is one earthquake, sized by the ${gterm('energy', 'seismic energy')} it released (top ${treeItems.length}). Because the scale is logarithmic, the largest events dwarf everything else. Click a tile for detail.</div>
      <div class="chart-wrap">${svgTreemap(treeItems, 900, 340)}</div>
    </div>`;

  const rerender = () => renderMagnitude(root);
  root.querySelector('#toggle-log')?.addEventListener('click', () => { logY = !logY; rerender(); });
  root.querySelectorAll('.tree-cell[data-id]').forEach((c) => c.addEventListener('click', () => {
    const q = list.find((x) => x.id === c.getAttribute('data-id'));
    if (q) openQuakeDrawer(q);
  }));
  void maxCount;
}

function stat(value: string, label: string, note: string): string {
  return `<div class="stat-tile"><div class="stat-value sm">${value}</div><div class="stat-label">${label}</div><div class="stat-note">${note}</div></div>`;
}
